import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  initializeAuth,
  indexedDBLocalPersistence
} from "firebase/auth";
import {
  equalTo,
  getDatabase,
  orderByChild,
  query,
  ref,
  set,
  update,
  push,
  get
} from "firebase/database";
import {
  collection,
  getFirestore,
  addDoc,
  setDoc,
  doc,
  Timestamp
} from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import { getStorage } from "firebase/storage";
import { firebaseConfig } from "../configs/authConfig";
import { Capacitor } from "@capacitor/core";

const whichAuth = (app: any) => {
  let auth;
  if (Capacitor.isNativePlatform()) {
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence
    });
  } else {
    auth = getAuth();
  }
  return auth;
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = whichAuth(app);
export const db = getDatabase(app);
export const firestore = getFirestore(app);
export const storage = getStorage();
export const analytics = getAnalytics(app);

const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async () => {
  try {
    const res = await signInWithPopup(auth, googleProvider);
    const user = res.user;
    if (user) return "success";
  } catch (err: any) {
    console.error(err);
  }
};

export const signOutAuth = async () => {
  try {
    await signOut(auth);
    window.location.reload();
  } catch (err: any) {
    console.error(err);
  }
};

export const signInWithEmail = async (
  email: string,
  password: string
): Promise<any> => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    // console.log(res);
    return "success";
  } catch (err: any) {
    console.error(err);
    if (err.code === "auth/user-not-found") {
      return "user-not-found";
    } else if (err.code === "auth/wrong-password") {
      return "wrong-password";
    }
  }
};

export const registerWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    return user.uid;
  } catch (err: any) {
    console.error(err);
  }
};

export const setAccount = async (uid: string, account: any) => {
  await set(ref(db, "accounts/" + uid), account);
};

export const updateAccount = async (uid: string, data: any) => {
  await update(ref(db, "accounts/" + uid), data);
};

export const updateBot = async (botId: string, data: any) => {
  await update(ref(db, "bots/" + botId), data);
};

export const deleteBot = async (botId: string) => {
  await set(ref(db, "bots/" + botId), null);
};

export const updateOrder = async (orderId: string, data: any) => {
  await update(ref(db, "orders/" + orderId), data);
};

export const deleteOrder = async (orderId: string) => {
  await set(ref(db, "orders/" + orderId), null);
};

export const setCustomTradeStrategies = async (
  botId: string,
  tradeId: string,
  data: any
) => {
  await set(ref(db, `botData/${botId}/customTradeStrategies/${tradeId}`), data);
};

export const createOrder = async (order: any) => {
  return await push(ref(db, "orders"), order);
};

export const setPath = async (path: string, data: any) => {
  await set(ref(db, path), data);
};

export const addBot = async (bot: any) => {
  return await push(ref(db, "bots"), bot);
};

export const addBotAction = async (botId: string, action: any) => {
  return await push(ref(db, `botData/${botId}/actions`), action);
};

export const addSavedStrategy = async (strategy: any) => {
  return await push(ref(db, "savedStrategies"), strategy);
};

export const addLobby = async (lobby: any) => {
  return await push(ref(db, "lobbies"), lobby);
};

export const sendPasswordReset = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err: any) {
    console.error(err);
  }
};

export const createActionByType = (
  actionType: string,
  userId: string,
  extend?: any
) => {
  const action = {
    timestamp: new Date().getTime(),
    action: actionType,
    status: "pending",
    user: userId,
    ...extend
  };

  return action;
};

export const getUserBotsRef = (uid: string) => {
  const botsRef = ref(db, `bots/`);
  return query(botsRef, orderByChild("user"), equalTo(uid));
};

export const getBotRef = (botId: string) => {
  return ref(db, `bots/${botId}`);
};

export const mapKeys = (data: { [key: string]: any }) => {
  return Object.keys(data).map(key => ({
    ...data[key],
    id: key
  }));
};

export const getUserLobbiesRef = (uid: string) => {
  return ref(db, `userLobbies/${uid}`);
};

export const getLobbyRef = (lobbyId: string) => {
  return ref(db, `lobbies/${lobbyId}`);
};

export const getUserRef = (uid: string) => {
  return ref(db, `accounts/${uid}`);
};

export const getUser = async (uid: string) => {
  const userRef = getUserRef(uid);
  const snapshot = await get(userRef);
  if (snapshot.exists()) {
    return snapshot.val();
  } else {
    console.log("User does not exist");
    return null;
  }
};

export const setFirebaseDocument = async (
  collectionName: string,
  data: any,
  docId?: string
) => {
  if (!docId) {
    const collectionRef = collection(firestore, collectionName);
    return await addDoc(collectionRef, data);
  } else {
    const docRef = doc(firestore, collectionName, docId);
    return await setDoc(docRef, data, { merge: true });
  }
};

export const convertTimestampsToDate: any = (obj: any) => {
  // Base case: if obj is a Timestamp, convert and return it as a Date
  if (obj instanceof Timestamp) {
    return obj.toDate();
  }

  // Recursive case: if obj is an array, map each element
  if (Array.isArray(obj)) {
    return obj.map(element => convertTimestampsToDate(element));
  }

  // Recursive case: if obj is an object, map each property
  if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        convertTimestampsToDate(value)
      ])
    );
  }

  // Base case: if obj is neither a Timestamp, array, nor object, return it unchanged
  return obj;
};
